// Utilitário de impressão de comandas em impressora térmica de cupom (58mm).
//
// Dois caminhos de impressão:
//  1. Dentro do Fully Kiosk Browser (tablet em modo kiosk): envia comandos
//     ESC/POS diretamente para a impressora Bluetooth (perfil SPP) usando a API
//     nativa `window.fully` — sem app externo (RawBT etc.).
//  2. Fora do Fully Kiosk (desktop/dev): gera um HTML compacto e imprime via
//     iframe oculto (window.print), preservando o comportamento anterior.

import { montarReciboComanda, montarReciboCobranca } from './escpos';

// ---------------------------------------------------------------------------
// Impressão Bluetooth nativa via Fully Kiosk Browser (ver. 2.0.31+ / 1.42+)
// ---------------------------------------------------------------------------

// UUID padrão do Serial Port Profile (SPP) — usado quando não há nome/MAC fixos.
const SPP_UUID = '00001101-0000-1000-8000-00805f9b34fb';

// Opcional: fixe o nome ou o MAC Bluetooth da impressora Goldensky para conexão
// direta e mais rápida. Se ambos ficarem vazios, tentamos autodetectar na lista
// de dispositivos pareados (ou caímos no SPP_UUID).
const IMPRESSORA_BT_NOME = '';
const IMPRESSORA_BT_MAC = '';

const TIMEOUT_CONEXAO_MS = 8000;

// Estado da API do Fully Kiosk para impressão Bluetooth:
//  'ausente'       -> window.fully indefinido (navegador comum, ou Interface
//                     JavaScript desligada no Fully)
//  'sem-bluetooth' -> window.fully existe, mas sem métodos de envio BT (versão
//                     antiga ou licença sem Bluetooth)
//  'ok'            -> pronto para imprimir via Bluetooth
function estadoFully() {
  if (typeof window === 'undefined' || typeof window.fully === 'undefined') return 'ausente';
  const f = window.fully;
  const temEnvio = typeof f.btSendHexData === 'function'
    || typeof f.btSendByteData === 'function'
    || typeof f.btSendStringData === 'function';
  return temEnvio ? 'ok' : 'sem-bluetooth';
}

function bytesParaHex(bytes) {
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += (bytes[i] & 0xff).toString(16).padStart(2, '0');
  }
  return hex;
}

// Envia os bytes usando o primeiro método de escrita BT disponível na versão
// instalada do Fully (hex é o mais confiável; byte/string como alternativa).
function enviarDadosBt(bytes) {
  const f = window.fully;
  if (typeof f.btSendHexData === 'function') return f.btSendHexData(bytesParaHex(bytes));
  if (typeof f.btSendByteData === 'function') return f.btSendByteData(new Uint8Array(bytes));
  if (typeof f.btSendStringData === 'function') {
    let s = '';
    for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i] & 0xff);
    return f.btSendStringData(s);
  }
  return false;
}

// Aviso não-bloqueante na tela (não interrompe o fluxo de venda).
// tipo: 'erro' (vermelho) | 'info' (azul) | 'ok' (verde).
function avisoTela(msg, tipo = 'erro') {
  if (typeof document === 'undefined') return;
  const cores = { erro: '#c0392b', info: '#1565c0', ok: '#2e7d32' };
  const el = document.createElement('div');
  el.textContent = msg;
  el.style.cssText = [
    'position:fixed', 'bottom:20px', 'left:50%', 'transform:translateX(-50%)',
    `background:${cores[tipo] || cores.erro}`, 'color:#fff', 'padding:12px 18px',
    'border-radius:8px', 'font-size:14px', 'font-family:sans-serif', 'z-index:99999',
    'box-shadow:0 2px 8px rgba(0,0,0,.3)', 'max-width:90%', 'text-align:center',
  ].join(';');
  document.body.appendChild(el);
  setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 5000);
}

// Tenta descobrir uma impressora entre os dispositivos Bluetooth pareados.
function autodetectarImpressora() {
  try {
    const lista = JSON.parse(window.fully.btGetDeviceListJson() || '[]');
    if (!Array.isArray(lista) || lista.length === 0) return null;
    const alvo = lista.find((d) => /print|pos|thermal|golden|58|80mm/i.test(d.name || ''));
    return (alvo || lista[0]) || null;
  } catch (e) {
    return null;
  }
}

// Estado de uma impressão pendente enquanto a conexão BT é estabelecida.
let pendente = null; // { bytes, resolve, reject, timer }
let handlersRegistrados = false;

function registrarHandlersBt() {
  if (handlersRegistrados) return;
  window.__reciboBt = {
    sucesso() {
      if (!pendente) return;
      const { bytes, resolve, reject, timer } = pendente;
      clearTimeout(timer);
      pendente = null;
      const ok = enviarDadosBt(bytes);
      if (ok !== false) resolve();
      else reject(new Error('Falha ao enviar dados para a impressora'));
    },
    falha() {
      if (!pendente) return;
      const { reject, timer } = pendente;
      clearTimeout(timer);
      pendente = null;
      reject(new Error('Não foi possível conectar à impressora Bluetooth'));
    },
  };
  window.fully.bind('onBtConnectSuccess', 'window.__reciboBt.sucesso();');
  window.fully.bind('onBtConnectFailure', 'window.__reciboBt.falha();');
  handlersRegistrados = true;
}

/**
 * Envia os bytes ESC/POS para a impressora Bluetooth via Fully Kiosk.
 * Reaproveita a conexão se já estiver aberta; caso contrário abre por
 * nome/MAC/UUID e aguarda o evento de conexão.
 * @param {number[]} bytes
 * @returns {Promise<void>}
 */
function imprimirBytes(bytes) {
  return new Promise((resolve, reject) => {
    // Só bloqueia se o Fully informar explicitamente que o Bluetooth está desligado.
    if (typeof window.fully.isBluetoothEnabled === 'function'
      && window.fully.isBluetoothEnabled() === false) {
      reject(new Error('Bluetooth desligado no tablet'));
      return;
    }

    // Conexão já aberta: envia direto.
    if (typeof window.fully.btIsConnected === 'function' && window.fully.btIsConnected()) {
      const ok = enviarDadosBt(bytes);
      if (ok !== false) resolve();
      else reject(new Error('Falha ao enviar dados para a impressora'));
      return;
    }

    registrarHandlersBt();

    const timer = setTimeout(() => {
      pendente = null;
      reject(new Error('Tempo esgotado ao conectar à impressora Bluetooth'));
    }, TIMEOUT_CONEXAO_MS);
    pendente = { bytes, resolve, reject, timer };

    try {
      if (IMPRESSORA_BT_MAC) {
        window.fully.btOpenByMac(IMPRESSORA_BT_MAC);
      } else if (IMPRESSORA_BT_NOME) {
        window.fully.btOpenByName(IMPRESSORA_BT_NOME);
      } else {
        const disp = autodetectarImpressora();
        if (disp && disp.mac) window.fully.btOpenByMac(disp.mac);
        else if (disp && disp.name) window.fully.btOpenByName(disp.name);
        else window.fully.btOpenByUuid(SPP_UUID);
      }
    } catch (e) {
      clearTimeout(timer);
      pendente = null;
      reject(new Error('Erro ao iniciar conexão Bluetooth: ' + (e && e.message)));
    }
  });
}

// Decide o caminho de impressão e SEMPRE dá retorno visual na tela, para que o
// operador nunca fique sem saber o que aconteceu ao tocar em Imprimir.
function imprimirRecibo(bytes, html) {
  const estado = estadoFully();
  if (estado === 'ok') {
    avisoTela('Enviando para a impressora...', 'info');
    imprimirBytes(bytes)
      .then(() => avisoTela('Recibo enviado à impressora', 'ok'))
      .catch((err) => avisoTela(
        'Impressão indisponível: ' + (err && err.message ? err.message : 'erro na impressora'),
      ));
    return;
  }
  if (estado === 'sem-bluetooth') {
    avisoTela('Fully Kiosk detectado, mas sem API Bluetooth. Ative a licença Plus e a opção Bluetooth nas configurações do Fully.');
    return;
  }
  // window.fully ausente: navegador comum OU Interface JavaScript do Fully desligada.
  avisoTela('Impressora Bluetooth não detectada (window.fully ausente). Ative "Enable JavaScript Interface" no Fully. Usando impressão do navegador.', 'info');
  dispararImpressao(html);
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

const ESTILOS = `
  @page { size: 58mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    width: 58mm;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #000;
    padding: 4px 5px 10px;
  }
  .center { text-align: center; }
  .estab { font-size: 14px; font-weight: 700; }
  .titulo { font-size: 13px; font-weight: 700; margin-top: 2px; }
  .hr { border-top: 1px dashed #000; margin: 6px 0; }
  .info div { margin: 1px 0; }
  .item { display: flex; gap: 4px; align-items: baseline; }
  .item .qtd { font-weight: 700; min-width: 22px; }
  .item .nome { flex: 1; word-break: break-word; }
  .item .preco { white-space: nowrap; }
  .obs { font-size: 11px; padding-left: 26px; font-style: italic; }
  .secao { font-weight: 700; font-size: 12px; margin: 6px 0 2px; }
  .secao small { font-weight: 400; display: block; font-size: 11px; }
  .total { display: flex; justify-content: space-between; font-weight: 700; font-size: 14px; margin-top: 6px; }
  .rodape { text-align: center; margin-top: 8px; font-size: 11px; }
  .dh { font-size: 11px; }
`;

function dispararImpressao(html) {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  const acionar = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } finally {
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }
  };

  if (iframe.contentWindow.document.readyState === 'complete') {
    setTimeout(acionar, 150);
  } else {
    iframe.onload = () => setTimeout(acionar, 150);
  }
}

function linhaItem(it, mostrarPrecos) {
  const qtd = it.quantidade ?? it.qtd ?? 1;
  const nome = esc(it.nome || it.produto_nome || '');
  const obs = it.observacao ? `<div class="obs">&#8627; ${esc(it.observacao)}</div>` : '';
  const preco = mostrarPrecos && it.subtotal != null
    ? `<span class="preco">${brl(it.subtotal)}</span>` : '';
  return `<div class="item"><span class="qtd">${qtd}x</span><span class="nome">${nome}</span>${preco}</div>${obs}`;
}

function brl(v) {
  const n = parseFloat(v);
  if (Number.isNaN(n)) return '';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Imprime uma comanda em formato de cupom 58mm.
 * @param {Object} opts
 * @param {string} [opts.titulo]        Título do cupom (ex.: "COMANDA" ou "PRODUÇÃO").
 * @param {string} [opts.estabelecimento]
 * @param {string|number} [opts.mesa]   Número da mesa.
 * @param {string|number} [opts.comanda] Número da comanda/cartão.
 * @param {string} [opts.cliente]
 * @param {Array}  opts.itens           [{ nome|produto_nome, quantidade|qtd, subtotal, observacao }]
 * @param {number} [opts.total]         Total (quando mostrarPrecos = true).
 * @param {boolean} [opts.mostrarPrecos] Exibe preços por item e total.
 * @param {string} [opts.rodape]
 */
export function imprimirComanda({
  titulo = 'COMANDA',
  estabelecimento = 'Parada Predileta',
  mesa,
  comanda,
  cliente,
  origem,
  itens = [],
  total = null,
  mostrarPrecos = true,
  rodape = '',
} = {}) {
  const dataHora = new Date().toLocaleString('pt-BR');

  const linhasItens = itens.map((it) => {
    const qtd = it.quantidade ?? it.qtd ?? 1;
    const nome = esc(it.nome || it.produto_nome || '');
    const obs = it.observacao ? `<div class="obs">&#8627; ${esc(it.observacao)}</div>` : '';
    const preco = mostrarPrecos && it.subtotal != null
      ? `<span class="preco">${brl(it.subtotal)}</span>` : '';
    return `<div class="item"><span class="qtd">${qtd}x</span><span class="nome">${nome}</span>${preco}</div>${obs}`;
  }).join('');

  const infoOrigem = origem ? `<div>Origem: <strong>${esc(origem)}</strong></div>` : '';
  const infoMesa = mesa != null ? `<div>Mesa: <strong>${esc(mesa)}</strong></div>` : '';
  const infoComanda = comanda != null ? `<div>Comanda: <strong>#${esc(comanda)}</strong></div>` : '';
  const infoCliente = cliente ? `<div>Cliente: <strong>${esc(cliente)}</strong></div>` : '';
  const linhaTotal = mostrarPrecos && total != null
    ? `<div class="total"><span>TOTAL</span><span>${brl(total)}</span></div>` : '';
  const blocoRodape = rodape ? `<div class="rodape">${esc(rodape)}</div>` : '';

  const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8" />
<style>${ESTILOS}</style></head>
<body>
  <div class="center estab">${esc(estabelecimento)}</div>
  <div class="center titulo">${esc(titulo)}</div>
  <div class="center dh">${esc(dataHora)}</div>
  <div class="hr"></div>
  <div class="info">${infoOrigem}${infoMesa}${infoComanda}${infoCliente}</div>
  <div class="hr"></div>
  <div class="itens">${linhasItens || '<div class="center">Sem itens</div>'}</div>
  ${linhaTotal}
  ${blocoRodape}
</body></html>`;

  const bytes = montarReciboComanda({
    titulo, estabelecimento, mesa, comanda, cliente, origem,
    itens, total, mostrarPrecos, rodape,
  });
  imprimirRecibo(bytes, html);
}

/**
 * Imprime um cupom com todos os pedidos que serão cobrados (mesa/balcão).
 * @param {Object} opts
 * @param {string} [opts.titulo]
 * @param {string} [opts.estabelecimento]
 * @param {string|number} [opts.mesa]
 * @param {Array}  [opts.grupos]  Comandas: [{ pedido_numero, comanda_numero, cliente_nome, itens }]
 * @param {Array}  [opts.balcaoItens] Itens avulsos/balcão: [{ nome, quantidade, subtotal }]
 * @param {number} [opts.total]
 */
export function imprimirCobranca({
  titulo = 'PEDIDOS A COBRAR',
  estabelecimento = 'Parada Predileta',
  mesa,
  grupos = [],
  balcaoItens = [],
  total = null,
} = {}) {
  const dataHora = new Date().toLocaleString('pt-BR');

  const blocosGrupos = grupos.map((g) => {
    const cabecalho = [
      g.pedido_numero != null ? `Pedido #${esc(g.pedido_numero)}` : '',
      g.comanda_numero != null ? `Comanda #${esc(g.comanda_numero)}` : '',
    ].filter(Boolean).join(' • ');
    const cliente = g.cliente_nome ? `<small>Cliente: ${esc(g.cliente_nome)}</small>` : '';
    const itens = (g.itens || []).map((it) => linhaItem(it, true)).join('')
      || '<div class="center">Sem itens</div>';
    return `<div class="secao">${cabecalho}${cliente}</div>${itens}`;
  }).join('<div class="hr"></div>');

  const temBalcao = (balcaoItens || []).length > 0;
  const blocoBalcao = temBalcao
    ? `${grupos.length ? '<div class="hr"></div>' : ''}<div class="secao">Balcão</div>${balcaoItens.map((it) => linhaItem(it, true)).join('')}`
    : '';

  const infoMesa = mesa != null ? `<div>Mesa: <strong>${esc(mesa)}</strong></div>` : '';
  const linhaTotal = total != null
    ? `<div class="total"><span>TOTAL</span><span>${brl(total)}</span></div>` : '';

  const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8" />
<style>${ESTILOS}</style></head>
<body>
  <div class="center estab">${esc(estabelecimento)}</div>
  <div class="center titulo">${esc(titulo)}</div>
  <div class="center dh">${esc(dataHora)}</div>
  <div class="hr"></div>
  <div class="info">${infoMesa}</div>
  <div class="hr"></div>
  ${blocosGrupos}
  ${blocoBalcao}
  ${linhaTotal}
</body></html>`;

  const bytes = montarReciboCobranca({
    titulo, estabelecimento, mesa, grupos, balcaoItens, total,
  });
  imprimirRecibo(bytes, html);
}
